import { traceRecorder } from './trace/traceRecorder';
import { traceStore } from './trace/traceStore';
import { telemetry } from './utils/telemetry';

export type SpatialState = 
    | "IDLE" 
    | "PLANNING"    // AI is formulating a navigation strategy
    | "EXECUTING"   // Tool Router is processing commands
    | "ANIMATING"   // Camera is actively in motion
    | "RE_TARGETING" // Camera is redirecting mid-flight
    | "COMPLETE"    // Navigation sequence reached destination
    | "USER_NAVIGATION" 
    | "COPILOT_NAVIGATION";

export type SpatialAuthority = "USER" | "VOICE" | "COPILOT" | "AI" | "SYNC" | "IDLE";

const AUTHORITY_PRIORITY: Record<SpatialAuthority, number> = {
    "USER": 5,
    "VOICE": 5,
    "COPILOT": 4,
    "SYNC": 3,
    "AI": 2,
    "IDLE": 0
};

const VALID_TRANSITIONS: Record<SpatialState, SpatialState[]> = {
    "IDLE": ["PLANNING", "EXECUTING", "ANIMATING", "USER_NAVIGATION", "COPILOT_NAVIGATION", "COMPLETE"],
    "PLANNING": ["EXECUTING", "IDLE", "ANIMATING"],
    "EXECUTING": ["ANIMATING", "RE_TARGETING", "IDLE", "COMPLETE"],
    "ANIMATING": ["PLANNING", "RE_TARGETING", "COMPLETE", "IDLE", "USER_NAVIGATION", "EXECUTING"],
    "RE_TARGETING": ["ANIMATING", "COMPLETE", "IDLE", "EXECUTING"],
    "COMPLETE": ["IDLE", "PLANNING", "ANIMATING", "USER_NAVIGATION", "EXECUTING"],
    "USER_NAVIGATION": ["IDLE", "PLANNING", "ANIMATING"],
    "COPILOT_NAVIGATION": ["PLANNING", "ANIMATING", "IDLE"]
};

const instanceId = `sm_${Math.random().toString(36).substr(2, 5)}`;
telemetry.forensic('STATE_MACHINE', 'INIT', { instanceId });

const _spatialStateMachine = {
    id: instanceId,
    state: "IDLE" as SpatialState,
    authority: "IDLE" as SpatialAuthority,
    history: ["IDLE"] as string[],
    listeners: [] as ((state: SpatialState) => void)[],

    getState() { return this.state; },
    getAuthority() { return this.authority; },
    getHistory() { return [...this.history]; },
    resetHistory() { this.history = [this.state]; },
    subscribe(cb: (state: SpatialState) => void) {
        this.listeners.push(cb);
        telemetry.debug('STATE_MACHINE', 'LISTENER_ADDED', { count: this.listeners.length });
        return () => { 
            this.listeners = this.listeners.filter(l => l !== cb); 
            telemetry.debug('STATE_MACHINE', 'LISTENER_REMOVED', { count: this.listeners.length });
        };
    },

    transition(to: SpatialState, authority: SpatialAuthority = "SYNC"): boolean {
        const from = this.state;
        const transitionId = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        
        telemetry.forensic('STATE_MACHINE', 'TRANSITION_REQUEST', {
            id: transitionId,
            from,
            to,
            requestedAuthority: authority,
            currentAuthority: this.authority,
            currentPriority: AUTHORITY_PRIORITY[this.authority],
            incomingPriority: AUTHORITY_PRIORITY[authority],
            timestamp: Date.now(),
            historyLength: this.history.length
        });
        
        // 1. Authority Check (Priority Enforcement)
        const currentPriority = AUTHORITY_PRIORITY[this.authority] || 0;
        let incomingPriority = AUTHORITY_PRIORITY[authority] || 0;

        // Special escalation: AI commands marked as 'essential' get priority 3.2 (beats SYNC)
        if (authority === "AI" && (to === "ANIMATING" || to === "PLANNING")) {
            incomingPriority = 3.2; 
        }

        if (incomingPriority < currentPriority && to !== "IDLE" && to !== "COMPLETE") {
            telemetry.warn('STATE_MACHINE', 'AUTHORITY_REJECTED', { to, from, authority, currentPriority, incomingPriority });
            traceStore.add({
                type: "STATE_TRANSITION",
                label: `AUTHORITY_REJECTED: ${from} -> ${to}`,
                details: { from, to, authority, currentPriority, incomingPriority },
                source: "SYSTEM"
            });
            return false;
        }

        // 2. Validation Check
        if (!VALID_TRANSITIONS[from].includes(to)) {
            // Special case: User can always break into USER_NAVIGATION from anywhere
            if (authority === "USER" && to === "USER_NAVIGATION") {
                // allow
            } else if (from === to) {
                // Allow self-transitions to update authority (e.g. AI Animating -> USER Animating)
                this.authority = authority;
                telemetry.info('STATE_MACHINE', 'SELF_TRANSITION', { to, authority });
                return true;
            } else {
                telemetry.warn('STATE_MACHINE', 'INVALID_TRANSITION', { from, to, authority });
                traceStore.add({
                    type: "STATE_TRANSITION",
                    label: `INVALID_STATE_TRANSITION: ${from} -> ${to}`,
                    details: { from, to, authority },
                    source: "SYSTEM"
                });
                return false;
            }
        }

        // 3. Execution
        this.state = to;
        this.authority = authority;
        this.history.push(`${from}->${to}(${authority})`);
        
        // Notify listeners
        this.listeners.forEach(l => l(this.state));
        
        if (this.history.length > 50) this.history.shift();
        
        telemetry.forensic('STATE_MACHINE', 'TRANSITION_EXECUTED', {
            instanceId: this.id,
            id: transitionId,
            from,
            to,
            authority,
            newState: this.state,
            timestamp: Date.now()
        });
        telemetry.info('STATE_MACHINE', 'STATE_UPDATE', { from, to, authority });

        // 4. Trace Integration
        traceStore.add({
            type: "STATE_TRANSITION",
            label: `STATE_TRANSITION: ${from} -> ${to}`,
            details: { from, to, authority },
            source: "SYSTEM"
        });

        // 5. Side Effects
        if (to === "USER_NAVIGATION") {
            // If user takes over, pause copilot
            if (typeof window !== 'undefined' && (window as any).WingmanCopilot) {
                (window as any).WingmanCopilot.pauseSession();
            }
        }

        return true;
    },

    // Shortcuts for common events
    onUserInteraction() {
        this.transition("USER_NAVIGATION", "USER");
    },

    onMoveEnd() {
        this.transition("IDLE", "IDLE");
    },

    onAnimationStart(authority: SpatialAuthority) {
        this.transition("ANIMATING", authority);
    },

    onComplete() {
        this.transition("IDLE", "IDLE");
    },

    forceSync() {
        const currentAuthority = this.authority;
        telemetry.debug('STATE_MACHINE', 'AUTHORITY_RELEASE_REQUEST', { currentAuthority });
        
        // CRITICAL RULE: If AI has authority, DO NOT reset to SYNC
        if (currentAuthority === "AI") {
            telemetry.debug('STATE_MACHINE', 'AUTHORITY_PROTECTED', { reason: 'AI_AUTHORITY_MAINTAINED' });
            return;
        }
        
        telemetry.info('STATE_MACHINE', 'FORCE_SYNC_RESET');
        this.authority = "SYNC";
    },

    /**
     * ATOM S1.5 — Cold Boot Reset
     * Deterministically restore machine to IDLE state.
     */
    /**
     * ATOM S1.5 — Cold Boot Reset
     * Deterministically restore machine to IDLE state.
     */
    purgeAll() {
        telemetry.warn('STATE_MACHINE', 'COLD_BOOT_RESET_START', { from: this.state });
        this.state = "IDLE";
        this.authority = "IDLE";
        this.history = ["IDLE"];
        telemetry.info('STATE_MACHINE', 'COLD_BOOT_RESET_COMPLETE');
    },

    /** @deprecated Use purgeAll() */
    reset() {
        this.purgeAll();
    }
};

// 🚀 SINGLETON ENFORCEMENT
// Ensures instance parity across multiple JS bundles (App vs HDRI Platform)
const globalStateMachine = (typeof window !== 'undefined' && (window as any).WingmanStateMachine)
    ? (window as any).WingmanStateMachine
    : _spatialStateMachine;

if (typeof window !== 'undefined') {
    (window as any).WingmanStateMachine = globalStateMachine;
    (window as any).spatialStateMachine = globalStateMachine;
}

export { globalStateMachine as spatialStateMachine };
